import { useEffect, useMemo, useState, useCallback } from 'react';
import { SearchInput } from '@aimsos/ui';

/**
 * In-panel conversation list (same flow as ann `ConversationHistoryView` — not a drawer).
 *
 * @param {object} props
 * @param {Array<object>} props.conversations
 * @param {string|null} props.selectedConversationId
 * @param {(id: string) => void} props.onSelectConversation
 * @param {boolean} [props.isLoading]
 * @param {string} props.searchPlaceholder
 * @param {string} props.emptyLabel
 * @param {string} props.emptySearchLabel
 * @param {string} props.loadingLabel
 * @param {unknown} [props.listFetchError] — when set (e.g. failed list API), show error instead of empty list
 * @param {string} [props.listFetchErrorLabel]
 * @param {Record<string, string>} props.groupLabels — keys: today, yesterday, week, month, quarter, older
 * @param {(agentId: string, userId: string) => void|Promise<void>} [props.fetchConversations]
 * @param {string|null} [props.agentIdForConversations]
 * @param {string} [props.userId]
 */

const getConvId = (c) =>
  c.conversationUuid || c.conversation_uuid || c.uuid || c.id || '';

const getConversationDate = (c) =>
  c.last_modified ||
  c.startedAt ||
  c.updated_at ||
  c.created_at ||
  c.date ||
  c.timestamp ||
  null;

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const groupConversationsByDate = (conversations, groupLabels) => {
  const now = new Date();
  const todayStart = startOfDay(now);

  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);

  const weekStart = new Date(todayStart);
  weekStart.setDate(weekStart.getDate() - todayStart.getDay());

  const thirtyDaysAgo = new Date(todayStart);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const ninetyDaysAgo = new Date(todayStart);
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

  const groups = [
    { key: 'today', label: groupLabels.today, items: [] },
    { key: 'yesterday', label: groupLabels.yesterday, items: [] },
    { key: 'week', label: groupLabels.week, items: [] },
    { key: 'month', label: groupLabels.month, items: [] },
    { key: 'quarter', label: groupLabels.quarter, items: [] },
    { key: 'older', label: groupLabels.older, items: [] },
  ];

  for (const conv of conversations) {
    const rawDate = getConversationDate(conv);
    const date = rawDate ? new Date(rawDate) : null;

    if (!date || Number.isNaN(date.getTime())) {
      groups[0].items.push(conv);
      continue;
    }

    const dayStart = startOfDay(date);

    if (dayStart >= todayStart) {
      groups[0].items.push(conv);
    } else if (dayStart >= yesterdayStart) {
      groups[1].items.push(conv);
    } else if (dayStart >= weekStart) {
      groups[2].items.push(conv);
    } else if (dayStart >= thirtyDaysAgo) {
      groups[3].items.push(conv);
    } else if (dayStart >= ninetyDaysAgo) {
      groups[4].items.push(conv);
    } else {
      groups[5].items.push(conv);
    }
  }

  return groups.filter((g) => g.items.length > 0);
};

const ChatConversationHistoryView = ({
  conversations = [],
  selectedConversationId,
  onSelectConversation,
  isLoading = false,
  searchPlaceholder,
  emptyLabel,
  emptySearchLabel,
  loadingLabel,
  listFetchError,
  listFetchErrorLabel,
  groupLabels,
  fetchConversations,
  agentIdForConversations,
  userId = '',
}) => {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!fetchConversations || !agentIdForConversations) return;
    if (conversations.length > 0) return;
    void fetchConversations(agentIdForConversations, userId);
  }, [agentIdForConversations, conversations.length, fetchConversations, userId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) => (c.title || getConvId(c) || '').toLowerCase().includes(q));
  }, [conversations, query]);

  const groups = useMemo(
    () => groupConversationsByDate(filtered, groupLabels),
    [filtered, groupLabels],
  );

  const handleSelect = useCallback(
    (id) => {
      if (id) onSelectConversation(id);
      setQuery('');
    },
    [onSelectConversation],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 pb-3">
        <SearchInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onClear={() => setQuery('')}
          placeholder={searchPlaceholder}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        {isLoading ? (
          <div className="space-y-2 px-0.5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-9 w-full animate-pulse rounded-lg bg-surface-hover"
                aria-hidden
              />
            ))}
            <p className="sr-only">{loadingLabel}</p>
          </div>
        ) : listFetchError && listFetchErrorLabel ? (
          <p
            className="px-2 py-10 text-center text-sm text-rose-600 dark:text-rose-400"
            role="alert"
          >
            {listFetchErrorLabel}
          </p>
        ) : groups.length === 0 ? (
          <p className="px-2 py-10 text-center text-sm text-content-muted">
            {query.trim() ? emptySearchLabel : emptyLabel}
          </p>
        ) : (
          <div className="space-y-2 pb-2">
            {groups.map((group) => (
              <section key={group.key} className="space-y-0.5">
                <h3 className="px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-content-muted">
                  {group.label}
                </h3>
                <ul className="space-y-0.5">
                  {group.items.map((c) => {
                    const id = getConvId(c);
                    if (!id) return null;
                    const active = id === selectedConversationId;
                    return (
                      <li key={id}>
                        <button
                          type="button"
                          onClick={() => handleSelect(id)}
                          className={[
                            'flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                            active
                              ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30'
                              : 'border-transparent hover:bg-surface-hover',
                          ].join(' ')}
                        >
                          <span className="min-w-0 flex-1 truncate font-medium text-content">
                            {c.title || id}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatConversationHistoryView;
